import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheckIcon,
  ShieldExclamationIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ExclamationTriangleIcon,
  CheckBadgeIcon,
} from '@heroicons/react/24/outline';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import ProviderTabs from '../../components/common/ProviderTabs';
import { microsoftAPI, googleAPI, oauthAPI } from '../../services/api';

export default function MFAStatusPage() {
  const [users, setUsers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [microsoftConnected, setMicrosoftConnected] = useState(false);
  const [googleConnected, setGoogleConnected] = useState(false);
  const [activeProvider, setActiveProvider] = useState(null);

  useEffect(() => {
    checkConnections();
  }, []);

  const checkConnections = async () => {
    try {
      const response = await oauthAPI.getStatus();
      const msConnected = response.data.microsoft?.connected || false;
      const gConnected = response.data.google?.connected || false;

      setMicrosoftConnected(msConnected);
      setGoogleConnected(gConnected);

      // Set default active provider based on what's connected
      if (msConnected) {
        setActiveProvider('microsoft');
      } else if (gConnected) {
        setActiveProvider('google');
      }

      if (msConnected || gConnected) {
        // Will load data after activeProvider is set
      } else {
        setLoading(false);
      }
    } catch (err) {
      setError('Failed to check connection status');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeProvider) {
      loadData();
    }
  }, [activeProvider]);

  const getAPI = () => {
    return activeProvider === 'google' ? googleAPI : microsoftAPI;
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const api = getAPI();
      const params = {};
      if (filter !== 'all') params.mfaStatus = filter;
      if (search) params.search = search;

      const [usersRes, summaryRes] = await Promise.all([
        api.getMFAUsers(params),
        api.getMFASummary(),
      ]);

      setUsers(usersRes.data.data.users);
      setSummary(summaryRes.data.data);
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to load MFA data';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      setError(null);
      const api = getAPI();
      await api.triggerSync('mfa');
      await loadData();
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Sync failed';
      setError(errorMessage);
    } finally {
      setSyncing(false);
    }
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
  };

  const handleProviderChange = (provider) => {
    setActiveProvider(provider);
    setFilter('all');
    setSearch('');
  };

  useEffect(() => {
    if (activeProvider && (microsoftConnected || googleConnected)) {
      const isCurrentProviderConnected =
        (activeProvider === 'microsoft' && microsoftConnected) ||
        (activeProvider === 'google' && googleConnected);

      if (isCurrentProviderConnected) {
        loadData();
      }
    }
  }, [filter, search]);

  const isGoogle = activeProvider === 'google';

  if (!microsoftConnected && !googleConnected && !loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">MFA Status</h1>
          <p className="mt-1 text-slate-500 dark:text-slate-400">
            View MFA enrollment status for all users in your organization.
          </p>
        </div>

        <Card className="text-center py-12">
          <ExclamationTriangleIcon className="h-12 w-12 mx-auto text-amber-500 mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
            No Cloud Provider Connected
          </h3>
          <p className="text-slate-500 dark:text-slate-400 mb-6">
            Connect Microsoft 365 or Google Workspace to view MFA status for your users.
          </p>
          <Link to="/dashboard/connect">
            <Button>Connect Cloud Provider</Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            {isGoogle ? '2-Step Verification Status' : 'MFA Status'}
          </h1>
          <p className="mt-1 text-slate-500 dark:text-slate-400">
            View {isGoogle ? '2-Step Verification' : 'MFA'} enrollment status for all users in your{' '}
            {isGoogle ? 'Google Workspace' : 'Microsoft 365'} tenant.
          </p>
        </div>
        <Button onClick={handleSync} loading={syncing} disabled={syncing || !activeProvider}>
          <ArrowPathIcon className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? 'Syncing...' : 'Sync Now'}
        </Button>
      </div>

      {/* Provider Tabs */}
      <ProviderTabs
        activeProvider={activeProvider}
        onProviderChange={handleProviderChange}
        microsoftConnected={microsoftConnected}
        googleConnected={googleConnected}
      />

      {/* Error Banner */}
      {error && (
        <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4">
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card>
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-700">
                <ShieldCheckIcon className="h-6 w-6 text-slate-600 dark:text-slate-400" />
              </div>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Total Users</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {summary.totalUsers}
                </p>
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-green-100 dark:bg-green-900/30">
                <ShieldCheckIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {isGoogle ? '2SV Enrolled' : 'MFA Enabled'}
                </p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {summary.mfaEnabled}
                </p>
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-red-100 dark:bg-red-900/30">
                <ShieldExclamationIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {isGoogle ? '2SV Not Enrolled' : 'MFA Disabled'}
                </p>
                <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                  {summary.mfaDisabled}
                </p>
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/30">
                {isGoogle && summary.mfaEnforced !== undefined ? (
                  <CheckBadgeIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                ) : (
                  <ShieldCheckIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                )}
              </div>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {isGoogle ? '2SV Enforced' : 'MFA Coverage'}
                </p>
                <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                  {isGoogle && summary.mfaEnforced !== undefined
                    ? summary.mfaEnforced
                    : `${summary.mfaPercentage}%`}
                </p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Filters and Search */}
      <Card>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <FunnelIcon className="h-5 w-5 text-slate-400" />
            <select
              value={filter}
              onChange={(e) => handleFilterChange(e.target.value)}
              className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">All Users</option>
              <option value="enabled">{isGoogle ? '2SV Enrolled' : 'MFA Enabled'}</option>
              <option value="disabled">{isGoogle ? '2SV Not Enrolled' : 'MFA Disabled'}</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Last Sync Info */}
      {summary?.lastSyncedAt && (
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Last synced: {new Date(summary.lastSyncedAt).toLocaleString()}
        </p>
      )}

      {/* Users Table */}
      <Card padding={false}>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-12">
            <ShieldCheckIcon className="h-12 w-12 mx-auto text-slate-400 mb-4" />
            <p className="text-slate-500 dark:text-slate-400">
              {summary?.totalUsers === 0
                ? `No users found. Click "Sync Now" to fetch data from ${isGoogle ? 'Google Workspace' : 'Microsoft 365'}.`
                : 'No users match your search criteria.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {isGoogle ? '2SV Status' : 'MFA Status'}
                  </th>
                  {isGoogle && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Enforced
                    </th>
                  )}
                  {!isGoogle && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Methods
                    </th>
                  )}
                  {isGoogle && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Role
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Last Synced
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                          <span className="text-sm font-medium text-primary-700 dark:text-primary-400">
                            {user.displayName?.charAt(0) || user.email?.charAt(0) || '?'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-slate-900 dark:text-white">
                            {user.displayName || 'Unknown'}
                          </p>
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            {user.email}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {user.hasMFA || user.isEnrolledIn2Sv ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                          <ShieldCheckIcon className="h-3.5 w-3.5" />
                          {isGoogle ? 'Enrolled' : 'Enabled'}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">
                          <ShieldExclamationIcon className="h-3.5 w-3.5" />
                          {isGoogle ? 'Not Enrolled' : 'Disabled'}
                        </span>
                      )}
                    </td>
                    {isGoogle && (
                      <td className="px-6 py-4">
                        {user.isEnforcedIn2Sv ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                            <CheckBadgeIcon className="h-3.5 w-3.5" />
                            Enforced
                          </span>
                        ) : (
                          <span className="text-sm text-slate-400">-</span>
                        )}
                      </td>
                    )}
                    {!isGoogle && (
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {user.mfaMethods && user.mfaMethods.length > 0 ? (
                            user.mfaMethods.map((method, idx) => (
                              <span
                                key={idx}
                                className="inline-block px-2 py-0.5 rounded text-xs bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300"
                              >
                                {method.type}
                              </span>
                            ))
                          ) : (
                            <span className="text-sm text-slate-400">None</span>
                          )}
                        </div>
                      </td>
                    )}
                    {isGoogle && (
                      <td className="px-6 py-4">
                        {user.isAdmin ? (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400">
                            Admin
                          </span>
                        ) : (
                          <span className="text-sm text-slate-400">User</span>
                        )}
                      </td>
                    )}
                    <td className="px-6 py-4 text-sm text-slate-500 dark:text-slate-400">
                      {user.lastSyncedAt
                        ? new Date(user.lastSyncedAt).toLocaleDateString()
                        : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
